import { Check, X } from "lucide-react";
import { useState } from "react";
import { Button, Input, TextField } from "react-aria-components";

import { Badge } from "@/components/badge";

import type { MBCReviewData } from "../types";

import { BriefText } from "./BriefText";
import { BriefTextEditor } from "./BriefTextEditor";
import { EditButton } from "./EditButton";

type BriefCardProps = {
  brief: MBCReviewData["brief"];
  /** When provided, the title becomes editable. */
  onTitleSave?: (value: string) => void;
  /** When provided, the visual direction becomes editable. */
  onVisualDirectionSave?: (value: string) => void;
};

type TitleEditorProps = {
  onCancel: () => void;
  onSave: (value: string) => void;
  value: string;
};

function TitleEditor({ onCancel, onSave, value }: TitleEditorProps) {
  const [draft, setDraft] = useState(value);

  const save = () => {
    const next = draft.trim();

    // A brief always keeps a title; clearing it is treated as a cancel.
    if (next && next !== value.trim()) onSave(next);
    onCancel();
  };

  return (
    <div className="flex flex-1 items-center gap-2">
      <TextField
        aria-label="title"
        autoFocus
        className="flex-1"
        onChange={setDraft}
        onKeyDown={(event) => {
          if (event.key === "Enter") save();
          if (event.key === "Escape") onCancel();
        }}
        value={draft}
      >
        <Input className="w-full rounded-md border border-border-brand bg-bg-primary px-2 py-1 text-xl font-semibold text-text-primary outline-hidden" />
      </TextField>
      <Button
        aria-label="Save title"
        className="cursor-pointer rounded-md p-1 text-fg-brand-primary outline-hidden transition-colors hover:bg-bg-primary-hover"
        onPress={save}
      >
        <Check className="size-4" />
      </Button>
      <Button
        aria-label="Cancel title"
        className="cursor-pointer rounded-md p-1 text-fg-quaternary outline-hidden transition-colors hover:bg-bg-primary-hover"
        onPress={onCancel}
      >
        <X className="size-4" />
      </Button>
    </div>
  );
}

export function BriefCard({
  brief,
  onTitleSave,
  onVisualDirectionSave,
}: BriefCardProps) {
  const [isEditingTitle, setIsEditingTitle] = useState(false);
  const [isEditingVisualDirection, setIsEditingVisualDirection] =
    useState(false);

  return (
    <section className="rounded-xl border border-border-primary bg-bg-primary">
      {/* Title row */}
      <div className="flex items-center gap-3 border-b border-border-secondary px-5 py-4">
        {onTitleSave && isEditingTitle ? (
          <TitleEditor
            onCancel={() => setIsEditingTitle(false)}
            onSave={onTitleSave}
            value={brief.title}
          />
        ) : (
          <h2 className="text-xl font-semibold text-text-primary">
            {brief.title}
          </h2>
        )}
        <Badge isRounded size="medium" variant="secondary">
          {brief.typeTag}
        </Badge>
        {onTitleSave && !isEditingTitle && (
          <div className="ml-auto">
            <EditButton label="title" onPress={() => setIsEditingTitle(true)} />
          </div>
        )}
      </div>

      <div className="flex flex-col gap-3 px-5 py-5">
        <div className="flex items-center gap-2">
          <h3 className="flex-1 text-lg font-semibold text-text-primary">
            Visual direction
          </h3>
          {onVisualDirectionSave && !isEditingVisualDirection && (
            <EditButton
              label="visual direction"
              onPress={() => setIsEditingVisualDirection(true)}
            />
          )}
        </div>
        {onVisualDirectionSave && isEditingVisualDirection ? (
          <BriefTextEditor
            onCancel={() => setIsEditingVisualDirection(false)}
            onSave={onVisualDirectionSave}
            placeholder="Describe the visual direction"
            value={brief.visualDirection}
          />
        ) : (
          <BriefText
            placeholder="No visual direction yet"
            value={brief.visualDirection}
          />
        )}
      </div>
    </section>
  );
}
